/**
 * AiChatPane - body component for the AI chat right pane.
 *
 * Session state (useChatSession) is owned at the ProjectPage level. The session
 * dropdown lives in the RightUtilityPane header via the chat RightRailItem's
 * headerAction slot. This component receives all session state and handlers as
 * props.
 *
 * Scope model:
 *   - PRIMARY scope  = project (projectUuid in selectedContext)
 *   - SECONDARY hint = current page/menu (pageScope prop, injected per-run only)
 *   - EXPLICIT items = user-selected repo items, Monaco ranges, etc.
 *                      (explicitContextItems array - displayed as chips)
 * The session persists across project menu navigation; pageScope is a retrieval
 * hint sent with each run, not a scope gate.
 *
 * Explicit context:
 *   explicitContextItems accumulate as the user interacts with project menus
 *   (clicking files/dirs in the repo tree, selecting Monaco line ranges, etc.).
 *   At send-time, they are merged into SelectedContext for the backend.
 *   The user can remove individual items or clear all via the chip bar.
 *
 * Props:
 *   state                  - chat reducer state from useChatSession (owned by ProjectPage)
 *   isConnected            - WebSocket live status
 *   isRunActive            - whether a run is currently executing
 *   sendRun                - start a new run
 *   cancelRun              - cancel the active run
 *   confirmRun             - confirm an awaiting-confirmation run
 *   denyRun                - deny an awaiting-confirmation run
 *   retryRun               - retry the last failed run
 *   updateContext          - update the session's selected context
 *   addExplicitContext     - add an explicit context item (from menu interaction)
 *   removeExplicitContext  - remove a single explicit context item by id
 *   clearExplicitContext   - clear all explicit context items
 *   projectUuid            - used to reset context to project-level on clear
 *   pageScope              - secondary context hint (current menu slug); injected per-run
 */

import type {
  AiMessageActionHandler,
  ChatMode,
  ExplicitContextItem,
  SelectedContext,
  UserPromptResponse,
} from '@protoss/shared';
import { getModelContextWindowTokens } from '@protoss/shared';
import * as React from 'react';
import { useWorkspace } from '../../../contexts/WorkspaceContext';
import type { SendRunOptions } from '../hooks/useChatSession';
import type { ChatState, ConversationTurn } from '../state/chatState';
import { ChatComposer } from './ChatComposer';
import { ChatMessageList } from './ChatMessageList';
import { RunStatusStrip } from './RunStatusStrip';
import { UserInteractionDrawer } from './UserInteractionDrawer';

/**
 * An externally-triggered run to inject into the chat pane.
 *
 * Callers (e.g. enrichment button, pipeline tools) set a new `ChatInjection`
 * with a unique `injectionId` to fire a run without typing in the composer.
 * Changing `injectionId` triggers the run; the pane deduplicates by ID so
 * remounting or re-renders do not cause double-fires.
 *
 * The `run` payload follows the same `SendRunOptions` contract as user input,
 * so module routing, context, and model overrides all apply identically.
 */
export interface ChatInjection {
  /** Unique identifier for this injection. Change this to trigger a new run. */
  injectionId: string;
  /** The run to dispatch when this injection fires. */
  run: SendRunOptions;
}

export interface AiChatPaneProps {
  state: ChatState;
  isConnected: boolean;
  isRunActive: boolean;
  sendRun: (opts: SendRunOptions) => void;
  cancelRun: () => void;
  confirmRun: () => void;
  denyRun: () => void;
  respondPrompt: (promptId: string, response: UserPromptResponse) => void;
  retryRun: (opts: SendRunOptions) => void;
  updateContext: (context: SelectedContext) => void;
  setDraftExplicitContext: (item: ExplicitContextItem) => void;
  confirmDraftExplicitContext: () => void;
  clearDraftExplicitContext: () => void;
  removeExplicitContext: (itemId: string) => void;
  clearExplicitContext: () => void;
  /** Load the next older page of history (scroll-to-top pagination) */
  loadOlderHistory: () => void;
  /** Primary project UUID - used to reset context to project scope on clear */
  projectUuid: string;
  /**
   * Current page/menu slug (e.g. 'repos', 'boards').
   * Injected as a secondary context hint with each run for retrieval gating.
   * Does NOT define the primary chat scope - the session remains project-scoped
   * across all menu navigation.
   */
  pageScope?: string;
  /** Focus the composer on mount (landing-page centered chat). */
  autoFocusComposer?: boolean;
  /** Setter for the shared composer mode (Ask/Agent/Auto). */
  setComposerMode?: (mode: ChatMode) => void;
  /**
   * External run injection.
   *
   * When set (or when `injectionId` changes), the pane fires the embedded run
   * automatically through the same `handleSend` pipeline used for user input.
   * This allows tools, buttons, and other surfaces to drive the chat without
   * reaching into the session directly.
   */
  injection?: ChatInjection;
  /**
   * Handler for structured message action buttons (Accept / Cancel).
   * The parent is responsible for applying enrichment output to form state.
   */
  onAction?: AiMessageActionHandler;
  /**
   * Callback to persist the user's action on a structured card to the DB.
   * Receives the run's messageId (UUID) and the action taken.
   */
  onActionSettled?: (
    runId: string,
    messageUuid: string,
    actionResult: 'accepted' | 'dismissed',
    acceptedFieldKeys: string[],
    cardKey?: string
  ) => void;
  /**
   * Optional node rendered between the message list and the composer.
   * Used for contextual UI like the generation pipeline TODO drawer.
   */
  bottomDrawer?: React.ReactNode;
  /**
   * Opt-in inline reconnect indicator in the composer toolbar (left of Send).
   * For surfaces whose pane has no header hosting ChatConnectionIndicator
   * (e.g. the sales landing page). Pass state.connectionStatus /
   * state.reconnectAttempt / manualReconnect from the chat session.
   */
  connectionStatus?: ChatState['connectionStatus'];
  reconnectAttempt?: number;
  onReconnect?: () => void;
}

/**
 * [Tool-driven refresh] AI tools that mutate workspace-level state the app
 * caches client-side (WorkspaceContext). When a run completes a call to one of
 * these, the workspace is refetched so dependent UI - e.g. the header logo
 * right after /init-workspace sets it - reflects the change without a reload.
 */
const WORKSPACE_REFRESH_TOOLS = new Set(['set_workspace_branding']);

/** Completed workspace-mutating tool calls across all turns (segments + steps). */
function countWorkspaceRefreshToolCompletions(turns: ConversationTurn[]): number {
  let count = 0;
  for (const turn of turns) {
    if (turn.kind !== 'assistant') continue;
    for (const segment of turn.segments ?? []) {
      if (
        segment.kind === 'tool' &&
        segment.status === 'completed' &&
        WORKSPACE_REFRESH_TOOLS.has(segment.toolName)
      ) {
        count++;
      }
    }
    for (const step of turn.executionStatus?.steps ?? []) {
      if (
        step.type === 'tool_execution' &&
        step.status === 'completed' &&
        WORKSPACE_REFRESH_TOOLS.has(step.toolName)
      ) {
        count++;
      }
      const toolCalls =
        (step as { toolCalls?: Array<{ toolName: string; status: string }> }).toolCalls ?? [];
      for (const call of toolCalls) {
        if (call.status === 'completed' && WORKSPACE_REFRESH_TOOLS.has(call.toolName)) {
          count++;
        }
      }
    }
  }
  return count;
}

export function AiChatPane({
  state,
  isConnected,
  isRunActive,
  sendRun,
  cancelRun,
  confirmRun,
  denyRun,
  respondPrompt,
  retryRun,
  updateContext,
  setDraftExplicitContext: _setDraftExplicitContext,
  confirmDraftExplicitContext,
  clearDraftExplicitContext,
  removeExplicitContext,
  clearExplicitContext,
  loadOlderHistory,
  projectUuid: _projectUuid,
  pageScope,
  autoFocusComposer,
  setComposerMode,
  injection,
  onAction,
  onActionSettled,
  bottomDrawer,
  connectionStatus,
  reconnectAttempt,
  onReconnect,
}: AiChatPaneProps) {
  // Derive the last failed run for the retry handler
  const lastFailedTurn = React.useMemo(() => {
    for (let i = state.turns.length - 1; i >= 0; i--) {
      const t = state.turns[i];
      if (t && t.kind === 'assistant' && t.lifecycleState === 'error') return t;
    }
    return undefined;
  }, [state.turns]);

  const handleRetry = React.useCallback(() => {
    if (!lastFailedTurn || lastFailedTurn.kind !== 'assistant') return;
    // Find the user turn immediately before this failed run
    let failedIdx = -1;
    for (let i = 0; i < state.turns.length; i++) {
      const t = state.turns[i];
      if (t && t.kind === 'assistant' && t.runId === lastFailedTurn.runId) {
        failedIdx = i;
      }
    }
    const userTurn = failedIdx > 0 ? state.turns[failedIdx - 1] : undefined;
    if (userTurn?.kind !== 'user') return;

    retryRun({
      prompt: userTurn.prompt,
      context: userTurn.context,
      model: lastFailedTurn.model,
      mode: lastFailedTurn.mode,
    });
  }, [lastFailedTurn, state.turns, retryRun]);

  // Wrap sendRun to inject pageScope as a secondary context hint per-run.
  // Explicit context merging (explicitContextItems + draftExplicitItem) is now
  // handled inside the hook's sendRun, so this wrapper only needs to add pageScope.
  const handleSend = React.useCallback(
    (opts: SendRunOptions) => {
      sendRun({
        ...opts,
        context: {
          ...opts.context,
          ...(pageScope ? { pageScope } : {}),
        },
      });
    },
    [sendRun, pageScope]
  );

  // External injection: fire the run once per unique injectionId.
  // Uses a ref to deduplicate - remounting or re-renders with the same ID
  // never cause double-fires. Runs through handleSend so context merging
  // and pageScope injection apply exactly as they do for user input.
  const firedInjectionIdRef = React.useRef<string | undefined>(undefined);
  React.useEffect(() => {
    if (!injection) return;
    if (injection.injectionId === firedInjectionIdRef.current) return;
    firedInjectionIdRef.current = injection.injectionId;
    handleSend(injection.run);
  }, [injection, handleSend]);

  // updateContext is kept for future use (e.g. context sync via WS)
  void updateContext;

  // [Tool-driven refresh] Refetch the workspace when a run completes a
  // workspace-mutating tool call (e.g. the init wizard setting the header
  // logo), so the header and other consumers update live - no reload needed.
  const { workspace, refetchWorkspace } = useWorkspace();
  const refreshToolCompletions = React.useMemo(
    () => countWorkspaceRefreshToolCompletions(state.turns),
    [state.turns]
  );
  const prevRefreshCountRef = React.useRef<number | null>(null);
  React.useEffect(() => {
    const prev = prevRefreshCountRef.current;
    prevRefreshCountRef.current = refreshToolCompletions;
    // React only to NEW completions (the initial measure is skipped). A jump
    // caused by history hydration costs one redundant idempotent refetch -
    // acceptable; missing the refresh at run end would not be.
    if (prev !== null && refreshToolCompletions > prev) {
      void refetchWorkspace();
    }
  }, [refreshToolCompletions, refetchWorkspace]);

  // [Context budget] How full the model's context window is, from the most recent run
  // that reported tokens. Use contextTokens (peak single-call input = real window
  // occupancy), NOT promptTokens - the latter SUMS input across every model call, so a
  // run that chunks a long transcript into many calls would falsely read as 100% full.
  // Fall back to promptTokens for legacy/rehydrated turns that predate contextTokens.
  const contextUsageRatio = React.useMemo(() => {
    for (let i = state.turns.length - 1; i >= 0; i--) {
      const t = state.turns[i];
      if (t?.kind !== 'assistant') continue;
      const occupancy = t.tokenUsage?.contextTokens ?? t.tokenUsage?.promptTokens;
      if (occupancy) {
        const windowTokens =
          workspace?.aiModels?.find((m) => m.id === t.model)?.contextWindowTokens ??
          getModelContextWindowTokens(t.model);
        return Math.min(1, occupancy / windowTokens);
      }
    }
    return null;
  }, [state.turns, workspace?.aiModels]);

  // The currently active run - drives the question drawer and the status strip.
  const activeRun = React.useMemo(() => {
    if (!state.activeRunId) return null;
    for (let i = state.turns.length - 1; i >= 0; i--) {
      const t = state.turns[i];
      if (t && t.kind === 'assistant' && t.runId === state.activeRunId) return t;
    }
    return null;
  }, [state.turns, state.activeRunId]);

  // [Conversational copilot] The active run's open question - rendered by the
  // UserInteractionDrawer above the composer (the single ask_user renderer).
  const activePendingPrompt =
    activeRun?.lifecycleState === 'awaiting_confirmation'
      ? (activeRun.pendingPrompt ?? null)
      : null;

  return (
    <div className="ai-chat-pane" role="region" aria-label="AI Chat">
      {/* Message list - grows to fill available height */}
      <ChatMessageList
        turns={state.turns}
        historyStatus={state.historyStatus}
        hasMoreHistory={state.hasMoreHistory}
        onLoadOlderHistory={loadOlderHistory}
        onConfirm={confirmRun}
        onDeny={denyRun}
        onRetry={handleRetry}
        onAction={onAction}
        onActionSettled={onActionSettled}
        onRespondPrompt={respondPrompt}
      />

      {/* Card wrapping the optional todo drawer + interaction drawer + composer */}
      <div className="ai-chat-bottom-card">
        {bottomDrawer}

        {/* Mid-run question panel - the single renderer for awaiting_input */}
        {activePendingPrompt ? (
          <UserInteractionDrawer
            prompt={activePendingPrompt}
            onRespond={respondPrompt}
            disabled={!isConnected}
          />
        ) : null}

        {/* Live run status - always directly above the input (below the
            question card when one is open). Passive: opens nothing; the full
            step trace lives in the conversation (RunStepChain). */}
        <RunStatusStrip run={activeRun} />

        {/* Composer - pinned at bottom */}
        <ChatComposer
          explicitContextItems={state.explicitContextItems}
          onRemoveExplicitContext={removeExplicitContext}
          onClearExplicitContext={clearExplicitContext}
          draftExplicitItem={state.draftExplicitItem}
          onConfirmDraftExplicitContext={confirmDraftExplicitContext}
          onClearDraftExplicitContext={clearDraftExplicitContext}
          isRunActive={isRunActive}
          isConnected={isConnected}
          autoFocus={autoFocusComposer}
          mode={state.composerMode}
          onModeChange={setComposerMode}
          onSend={handleSend}
          onStop={cancelRun}
          promptPending={Boolean(activePendingPrompt)}
          contextUsageRatio={contextUsageRatio}
          connectionStatus={connectionStatus}
          reconnectAttempt={reconnectAttempt}
          onReconnect={onReconnect}
        />
      </div>
    </div>
  );
}
